import React, { useEffect, useMemo, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import LightHorizontalLogo from "../../logos/logo-v2-light-horizontal.png";
import DarkHorizontalLogo from "../../logos/logo-v2-dark-horizontal.png";
import Red1000PngLogo from "../../logos/red@1000.png";
import { useACTprepTheme } from "../../context/ACTprepContextPackage";
import { useProfile } from "../../context/ProfileContext";

import layout from "../../constants/layout";
import palette from "../../constants/palette";
import AccountModal, { accountModalTabs } from "../AccountModal/AccountModal";
import useDisclosure from "../../hooks/ui/useDisclosure";
import { useNavContext } from "../../context/NavContext";
import useStreamClient from "../../hooks/api/useStreamClient";
import {
  setSuppressInAppMessageBanners,
  subscribeToMessagingUnreadCount,
} from "../../hooks/api/useMessagingNotifyWebSocket";
import { Chat, useChatContext } from "stream-chat-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import { Close, Message } from "@mui/icons-material";
import { ALWAYS_TEMPORARY } from "../NavDrawer/NavDrawer";
import Messages from "../Messages";

const settings = [
  accountModalTabs.PROFILE,
  accountModalTabs.SECURITY,
  accountModalTabs.NOTIFICATIONS,
  accountModalTabs.LOGOUT,
];
const MESSAGING_GROUPS = ["Students", "Parents", "Employees"];

function StreamUnreadMessagesButton({ onClick, onUnreadChange, unreadCount }) {
  const { client } = useChatContext();
  useEffect(() => {
    if (!client) return;

    const initialCount = client.user?.total_unread_count;
    if (typeof initialCount === "number") {
      onUnreadChange(initialCount);
    }

    const updateUnreadCount = (event) => {
      const nextCount = event?.total_unread_count;
      if (typeof nextCount === "number") {
        onUnreadChange(nextCount);
      }
    };
    const subscription = client.on(updateUnreadCount);
    return () => {
      subscription.unsubscribe();
    };
  }, [client, onUnreadChange]);

  const displayUnreadCount = typeof unreadCount === "number" ? unreadCount : 0;

  return (
    <Badge
      badgeContent={displayUnreadCount}
      color="error"
      max={99}
      overlap="circular"
      invisible={displayUnreadCount <= 0}
    >
      <IconButton onClick={onClick} sx={{ p: 1 }}>
        <Message />
      </IconButton>
    </Badge>
  );
}

function NavBar({ navDrawerOpen, setNavDrawerOpen }) {
  const { theme } = useACTprepTheme();
  const { profile, isLoaded: isProfileLoaded } = useProfile();
  const isSmUp = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const [firstAccountModalPage, setFirstAccountModalPage] = useState(
    accountModalTabs.PROFILE
  );
  const {
    isOpen: AccountModalOpen,
    onOpen: onOpenAccountModal,
    onClose: onCloseAccountModal,
  } = useDisclosure();
  const messagesDisclosure = useDisclosure();
  const [createChannelTrigger, setCreateChannelTrigger] = useState(0);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [streamTotalUnread, setStreamTotalUnread] = useState(0);
  const { isNavDisabled } = useNavContext();
  const { client: streamClient } = useStreamClient();

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMenuItemClick = (setting) => {
    handleCloseUserMenu();
    setFirstAccountModalPage(setting);
    onOpenAccountModal();
  };

  // Timezone state & browser detection
  const browserTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );

  const badgeCount = isProfileLoaded
    ? 3 -
    (Boolean(profile?.email_verified) +
      Boolean(!profile.phone_number || profile?.phone_number_verified) +
      Boolean(profile?.timezone == browserTimeZone))
    : 0;
  const canUseMessaging = MESSAGING_GROUPS.some((group) =>
    profile?.groups?.includes(group)
  );
  const isEmployee = profile?.groups?.includes("Employees");

  useEffect(() => {
    const baseTitle = "Admin | Outliers ACT Prep";
    document.title =
      streamTotalUnread > 0 ? `(${streamTotalUnread}) ${baseTitle}` : baseTitle;
  }, [streamTotalUnread]);

  useEffect(() => {
    setSuppressInAppMessageBanners(messagesDisclosure.isOpen);
    return () => {
      setSuppressInAppMessageBanners(false);
    };
  }, [messagesDisclosure.isOpen]);

  useEffect(() => {
    if (!canUseMessaging) {
      return;
    }
    return subscribeToMessagingUnreadCount((nextCount) => {
      if (typeof nextCount === "number") {
        if (streamClient?.user) {
          streamClient.user.total_unread_count = nextCount;
        }
        setStreamTotalUnread(nextCount);
      }
    });
  }, [canUseMessaging, streamClient]);

  return (
    <>
      <AppBar
        sx={(theme) => ({
          zIndex: theme.zIndex.drawer + 1,
          top: 0,
          position: "fixed",
          height: `${layout.navBarHeight}rem`,
          backgroundColor:
            theme.palette.mode === "light" ? palette.white : palette.grayDark,
        })}
        elevation={1}
      >
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            padding: "0 1rem",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <Box
            sx={{
              flexGrow: 0,
              display: {
                xs: "flex",
                sm: "flex",
                md: "flex",
                lg: ALWAYS_TEMPORARY ? "flex" : "none",
              },
            }}
          >
            <IconButton
              size="large"
              onClick={() => setNavDrawerOpen(!navDrawerOpen)}
              disabled={isNavDisabled}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <Stack alignItems={"center"} flexDirection={"row"}>
              <Box
                component="img"
                sx={{
                  height: `${layout.navBarHeight - 0.25}rem`,
                  padding: ".25rem",
                }}
                alt="Outliers ACT Prep Logo"
                src={
                  isSmUp
                    ? theme == "dark"
                      ? LightHorizontalLogo
                      : DarkHorizontalLogo
                    : Red1000PngLogo
                }
              />
            </Stack>
          </Box>
          <Box
            sx={{ flexGrow: 0, display: "flex", alignItems: "center", gap: 1 }}
          >
            {canUseMessaging && (
              <Tooltip title="Messages">
                {streamClient ? (
                  <Chat client={streamClient}>
                    <StreamUnreadMessagesButton
                      onClick={messagesDisclosure.onOpen}
                      onUnreadChange={setStreamTotalUnread}
                      unreadCount={streamTotalUnread}
                    />
                  </Chat>
                ) : (
                  <IconButton onClick={messagesDisclosure.onOpen} sx={{ p: 1 }}>
                    <Message />
                  </IconButton>
                )}
              </Tooltip>
            )}
            <Tooltip title="Open settings">
              <Badge
                badgeContent={badgeCount}
                color="error"
                anchorOrigin={{
                  horizontal: "right",
                  vertical: "top",
                }}
              >
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar alt="Profile Picture" src={profile.profile_pic_url}>
                    {(profile?.user?.first_name?.[0] || "") +
                      (profile?.user?.last_name?.[0] || "")}
                  </Avatar>
                </IconButton>
              </Badge>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem
                key={accountModalTabs.PROFILE}
                onClick={() => handleMenuItemClick(accountModalTabs.PROFILE)}
              >
                <Badge
                  badgeContent={badgeCount}
                  color="error"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <Typography textAlign="center" sx={{ pr: 1 }}>
                    {accountModalTabs.PROFILE}
                  </Typography>
                </Badge>
              </MenuItem>
              {settings.slice(1).map((setting) => (
                <MenuItem
                  key={setting}
                  onClick={() => handleMenuItemClick(setting)}
                >
                  <Typography textAlign="center">{setting}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <AccountModal
        isOpen={AccountModalOpen}
        onClose={onCloseAccountModal}
        firstPage={firstAccountModalPage}
      />
      {canUseMessaging && (
        <Dialog
          {...messagesDisclosure.getDisclosureProps()}
          maxWidth="lg"
          fullWidth
          fullScreen={!isSmUp}
          slotProps={{
            paper: {
              sx: {
                minHeight: { xs: "100%", sm: 560 },
                height: { xs: "100%", sm: "75vh" },
              },
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 1 }}>
            Messages
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isEmployee && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setCreateChannelTrigger((value) => value + 1)}
                >
                  + New Chat
                </Button>
              )}
              <IconButton
                aria-label="Close messages"
                size="small"
                onClick={messagesDisclosure.onClose}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
            }}
          >
            <Messages createChannelTrigger={createChannelTrigger} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default NavBar;
